import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Command } from 'cmdk'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Circle, CircleCheck, CornerDownRight, Frame, NotebookText, Play, Search, StickyNote, Type } from 'lucide-react'
import { AreaIcon } from '@/src/renderer/components/icons/AreaIcon'
import { ShortcutBadge } from '@/src/renderer/components/ui/Button/ShortcutBadge'
import { Switch } from '@/src/renderer/components/ui/switch'
import { usePlatform } from '@/src/renderer/hooks/usePlatform'
import { useScrollbarVisibility } from '@/src/renderer/hooks/useScrollbarVisibility'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { CanvasNote, LifeSpace, Project, Task } from '@/src/renderer/lib/db/models'
import { favoriteTaskPlace } from '@/src/renderer/lib/favorites'
import { formatFocusAgainstEstimate, formatFocusDuration, isOverEstimate } from '@/src/renderer/lib/focus/formatFocusDuration'
import { readRecent } from '@/src/renderer/lib/recent/recentItems'
import {
  canvasNoteHeadline, highlightParts, isActiveProject, isOpenTask, latestProjectList, rankText, searchExcerpt, searchProjectList, searchTaskList,
  searchWords,
} from '@/src/renderer/lib/search/matchSearch'
import type { SearchHit, SearchPage } from '@/src/renderer/lib/taskJump/openSearchHit'
import './CommandPalette.scss'

/** Die **Trefferart** unter dem Feld (CONTEXT.md). */
export type SearchKind = 'all' | 'task' | 'project' | 'note' | 'focus'

export interface CommandPaletteContentProps {
  /** Dessen Treffer stehen vorn; seine Seiten sind die „Gehe zu“-Aktionen. */
  activeLifeSpace: string
  /** Die Lebensbereiche mit ihren Namen — für die graue Ortsangabe. */
  lifeSpaces: ReadonlyArray<{ id: string; name: string }>
  /** Ein Treffer wurde geöffnet (↵ oder Klick; `newTab` bei ⌥). Das Overlay schließt danach selbst. */
  onSelect: (hit: SearchHit, options: { newTab: boolean }) => void
  /** ⌘↵ an einer Fokus-Zeile (Trefferart Fokus, „Zuletzt im Fokus“): Fokus starten. */
  onStartFocus: (taskId: string) => void
  /** Mit `false`: schließen. */
  onOpenChange: (open: boolean) => void
  /** Vorbelegung für Stories und Tests. */
  defaultQuery?: string
  defaultKind?: SearchKind
  defaultIncludeCompleted?: boolean
}

// 30 je Art, beim Tippen wie davor (gewünscht 02.10.2026) — mehr rollt die Liste.
const LIMITS = { task: 30, project: 30, note: 30 } as const
/** Unter „Alle“ steht je Treffergruppe so viel, darunter „Alle n zeigen“. */
const PREVIEW = 3
/** Vor dem Tippen zeigt eine einzelne Trefferart so viele ihrer letzten Einträge. */
const LATEST = 30
const DEBOUNCE_MS = 60
// Fokus VOR „Alle“, durch einen Strich getrennt: ← vom Standard „Alle“ führt direkt dorthin (gewünscht 02.10.2026).
const KINDS: SearchKind[] = ['focus', 'all', 'task', 'project', 'note']
/** „Zuletzt im Fokus“ unter „Alle“ vor dem Tippen. */
const RECENT_FOCUS = 3
const ACTION_PAGES: SearchPage[] = ['upcoming', 'quick-notes', 'dormant', 'recurring', 'goals', 'done']
const PAGE_TITLES: Record<SearchPage, string> = {
  upcoming: 'sidebar.upcoming',
  'quick-notes': 'sidebar.quickNotes',
  dormant: 'sidebar.dormant',
  recurring: 'sidebar.recurring',
  goals: 'sidebar.goals',
  done: 'sidebar.done',
}
const ICON = 16

const isLifeSpace = (id: string): id is LifeSpace => id === 'personal' || id === 'work' || id === 'home'

interface Row {
  value: string
  hit?: SearchHit
  /** Eine Zeile „Alle n … zeigen“: ↵ wechselt die Trefferart. */
  moreKind?: Exclude<SearchKind, 'all' | 'focus'>
  icon: React.ReactNode
  /** Das Icon in der Farbe des Lebensbereichs — bei Projekten, wie der Ordner in der Sidebar. */
  tintIcon?: boolean
  /** Färbt die Markierung in der Farbe dieses Bereichs — man sieht ohne zu lesen, wohin es geht. */
  lifeSpace?: string
  title: string
  /** Wo es steht, grau: Projekt oder Seite, bei einer Unteraufgabe die Elternaufgabe. */
  location: string
  /** Der Name des Lebensbereichs — am Zeilenende, in seiner Farbe. */
  spaceLabel: string
  excerpt?: string
  done: boolean
  /** Eine Fokus-Zeile: rechts die Fokuszeit gegen die Dauer. */
  focusTime?: { taskId: string; estimate: number | null; runningSince: Date | null }
  /** Offene Fokus-Zeile, die gerade nicht läuft: ⌘↵ startet den Fokus. Andere Zeilen öffnen nur. */
  focusTaskId?: string
}

interface Section { key: string; heading: string; rows: Row[] }

interface Results { query: string; tasks: Task[]; notes: CanvasNote[] }

const Highlight: React.FC<{ text: string; words: readonly string[] }> = ({ text, words }) => (
  <>
    {highlightParts(text, words).map((part, i) => (part.hit
      ? <mark key={i} className="command-palette__mark">{part.text}</mark>
      : <React.Fragment key={i}>{part.text}</React.Fragment>))}
  </>
)

/**
 * Die Fokuszeit einer Fokus-Zeile, wie an der Dauer-Marke: „9s von 10m“, über der Dauer in Warnfarbe;
 * ohne Dauer „9s fokussiert“. Der laufende Fokus zählt hier sekündlich — nur diese Zeile rendert neu.
 */
const FocusTime: React.FC<{ booked: number; estimate: number | null; runningSince: Date | null }> = ({ booked, estimate, runningSince }) => {
  const { t } = useTranslation()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!runningSince) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [runningSince])
  const seconds = booked + (runningSince ? Math.max(0, (now - new Date(runningSince).getTime()) / 1000) : 0)
  return (
    <span className="command-palette__row-time" data-over={isOverEstimate(seconds, estimate) ? '' : undefined}>
      {estimate ? formatFocusAgainstEstimate(seconds, estimate, t) : t('search.focusedTime', { time: formatFocusDuration(seconds, t) })}
    </span>
  )
}

function taskIcon(task: Task): React.ReactNode {
  if (!isOpenTask(task)) return <CircleCheck size={ICON} />
  return task.parentTaskId ? <CornerDownRight size={ICON} /> : <Circle size={ICON} />
}

function noteIcon(note: CanvasNote): React.ReactNode {
  if (note.kind === 'memo') return <NotebookText size={ICON} />
  if (note.kind === 'text') return <Type size={ICON} />
  if (note.kind === 'area') return <Frame size={ICON} />
  return <StickyNote size={ICON} />
}

/**
 * **Der Inhalt der Suche** (#233, CONTEXT.md → Suche): Feld, Trefferart, Treffergruppen, Fußleiste.
 * Kennt keine Hülle — `CommandPaletteDialog` legt ihn ins Overlay, mobil später ein Sheet.
 *
 * cmdk liefert nur Tastatur und Listen-ARIA (`shouldFilter={false}`); Treffer und Reihenfolge
 * kommen aus der Datenquelle (`lib/search/matchSearch`). Vor dem Tippen steht „Zuletzt geöffnet“.
 *
 * @example
 * <CommandPaletteContent activeLifeSpace="personal" lifeSpaces={spaces}
 *   onSelect={(hit, { newTab }) => open(hit, newTab)} onStartFocus={playTask} onOpenChange={setOpen} />
 */
export const CommandPaletteContent: React.FC<CommandPaletteContentProps> = ({
  activeLifeSpace,
  lifeSpaces,
  onSelect,
  onStartFocus,
  onOpenChange,
  defaultQuery = '',
  defaultKind = 'all',
  defaultIncludeCompleted = false,
}) => {
  const { t } = useTranslation()
  const { isMac } = usePlatform()
  const source = useDataSource()

  const [query, setQuery] = useState(defaultQuery)
  const [kind, setKind] = useState<SearchKind>(defaultKind)
  const [includeCompleted, setIncludeCompleted] = useState(defaultIncludeCompleted)
  const [results, setResults] = useState<Results | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Dieselbe Bildlaufleiste wie Sidebar und Hauptbereich — sichtbar nur beim Scrollen (gewünscht 02.10.2026).
  const listRef = useRef<HTMLDivElement>(null)
  const { visible: scrollbarVisible, onScroll: onListScroll } = useScrollbarVisibility({ ref: listRef })

  /* ── Abfrage: entprellt, eine veraltete Antwort fällt weg ── */
  const requestRef = useRef(0)
  useEffect(() => {
    const request = ++requestRef.current
    if (searchWords(query).length === 0) {
      setResults(null)
      return
    }
    const options = { includeCompleted, activeLifeSpace }
    const timer = window.setTimeout(() => {
      void Promise.all([
        source.searchTasks(query, { ...options, limit: LIMITS.task }),
        source.searchCanvasNotes(query, { ...options, limit: LIMITS.note }),
      ]).then(([tasks, notes]) => {
        if (request === requestRef.current) setResults({ query, tasks, notes })
      })
    }, DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [query, includeCompleted, activeLifeSpace, source])

  /*
    ── Vor dem Tippen, eine Trefferart: ihre letzten Einträge (gewünscht 02.10.2026) ──
    Schon beim Öffnen geholt, nicht erst beim Wechsel — ←/→ zeigen sie sofort.
  */
  // `focused` ganz, nicht nur 11: Unter „Fokus“ sucht das Tippen in allen Aufgaben, die je im Fokus waren.
  const [latest, setLatest] = useState<{ tasks: Task[]; notes: CanvasNote[]; focused: Task[] } | null>(null)
  useEffect(() => {
    let current = true
    const options = { includeCompleted, limit: LATEST }
    void Promise.all([
      source.latestTasks(options),
      source.latestCanvasNotes(options),
      source.latestFocusedTasks({ includeCompleted }),
    ]).then(([tasks, notes, focused]) => {
      if (current) setLatest({ tasks, notes, focused })
    })
    return () => { current = false }
  }, [includeCompleted, source])
  const running = source.useRunningFocus()

  /* ── Was die Zeilen zum Beschriften brauchen ── */
  const projects = source.useProjects()
  const projectsById = useMemo(() => new Map((projects ?? []).map(p => [p.id, p])), [projects])
  const recent = useMemo(() => readRecent(), [])
  const recentTaskIds = useMemo(() => recent.filter(item => item.kind === 'task').map(item => item.id), [recent])
  const recentTaskMap = source.useTasksByIds(recentTaskIds)
  const openedTasks = useMemo(
    () => recentTaskIds.map(id => recentTaskMap?.get(id)).filter((task): task is Task => !!task && !task.deletedAt),
    [recentTaskIds, recentTaskMap],
  )
  const openedProjects = useMemo(
    () => recent
      .filter(item => item.kind === 'project')
      .map(item => projectsById.get(item.id))
      .filter((project): project is Project => !!project && !project.deletedAt),
    [recent, projectsById],
  )
  const recentTasks = useMemo(() => openedTasks.slice(0, 4), [openedTasks])
  const recentProjects = useMemo(() => openedProjects.slice(0, 3), [openedProjects])
  // Unteraufgaben brauchen ihre Kette: Der Ort kommt von der obersten Aufgabe, dazu die Elternaufgabe.
  const subtaskIds = useMemo(
    () => [...(results?.tasks ?? []), ...openedTasks, ...(latest?.tasks ?? []), ...(latest?.focused ?? [])]
      .filter(task => task.parentTaskId)
      .map(task => task.id),
    [results, openedTasks, latest],
  )
  const chains = source.useTaskChains(subtaskIds)

  const spaceName = useCallback((id: string) => lifeSpaces.find(space => space.id === id)?.name ?? '', [lifeSpaces])
  const pageName = useCallback((page: SearchPage) => t(PAGE_TITLES[page]), [t])
  const projectName = useCallback((project: Project | undefined) => project?.name || t('project.defaultName'), [t])

  /* ── Zeilen ── */
  const taskRow = useCallback((task: Task, words: readonly string[], prefix = ''): Row => {
    const chain = chains?.get(task.id)
    const root = chain?.at(-1) ?? task
    const place = favoriteTaskPlace(root)
    const project = place.kind === 'project' ? projectsById.get(place.projectId) : undefined
    const lifeSpace = project?.lifeSpace ?? root.lifeSpace
    const location = [
      place.kind === 'project' ? projectName(project) : pageName(place.kind),
      task.parentTaskId ? chain?.[1]?.title : undefined,
    ].filter(Boolean).join(' · ')
    return {
      value: `${prefix}task:${task.id}`,
      hit: { kind: 'task', task },
      icon: taskIcon(task),
      lifeSpace,
      title: task.title,
      location,
      spaceLabel: spaceName(lifeSpace),
      excerpt: words.length > 0 && rankText(task.title, '', words) === null ? searchExcerpt(task.description ?? '', words) : undefined,
      done: !isOpenTask(task),
    }
  }, [chains, projectsById, spaceName, projectName, pageName])

  // Nur hier startet ⌘↵ den Fokus (gewünscht 02.10.2026): Sonst ist das Öffnen der Aufgabe wichtiger.
  const focusRow = useCallback((task: Task, words: readonly string[], prefix = ''): Row => {
    const runningSince = running?.taskId === task.id ? running.startTime : null
    return {
      ...taskRow(task, words, `${prefix}focus-`),
      focusTime: { taskId: task.id, estimate: task.estimatedDuration, runningSince },
      focusTaskId: isOpenTask(task) && !runningSince ? task.id : undefined,
    }
  }, [taskRow, running])

  const projectRow = useCallback((project: Project, prefix = ''): Row => ({
    value: `${prefix}project:${project.id}`,
    hit: { kind: 'project', project },
    icon: <AreaIcon size={ICON} />,
    tintIcon: true,
    lifeSpace: project.lifeSpace,
    title: projectName(project),
    location: isActiveProject(project) ? '' : t(project.status === 'archived' ? 'projectDetails.status.archived' : 'projectDetails.status.completed'),
    spaceLabel: spaceName(project.lifeSpace),
    done: !isActiveProject(project),
  }), [projectName, spaceName, t])

  const noteRow = useCallback((note: CanvasNote, words: readonly string[]): Row | null => {
    const project = projectsById.get(note.projectId)
    if (!project) return null
    const titleHit = rankText(note.title, '', words) !== null
    return {
      value: `note:${note.id}`,
      hit: { kind: 'note', note, project },
      icon: noteIcon(note),
      lifeSpace: project.lifeSpace,
      title: canvasNoteHeadline(note),
      location: projectName(project),
      spaceLabel: spaceName(project.lifeSpace),
      excerpt: words.length > 0 && note.title?.trim() && note.kind !== 'area' && !titleHit ? searchExcerpt(note.content, words) : undefined,
      done: !isActiveProject(project),
    }
  }, [projectsById, spaceName, projectName])

  const actionRow = useCallback((page: SearchPage, lifeSpace: LifeSpace): Row => ({
    value: `action:${page}`,
    hit: { kind: 'page', page, lifeSpace },
    icon: <ArrowRight size={ICON} />,
    lifeSpace,
    title: t('search.actions.goTo', { page: pageName(page) }),
    location: '',
    spaceLabel: spaceName(lifeSpace),
    done: false,
  }), [t, pageName, spaceName])

  const words = useMemo(() => searchWords(results?.query ?? ''), [results])
  const groups = useMemo(() => {
    if (!results) return null
    const options = { includeCompleted, activeLifeSpace, limit: LIMITS.project }
    return {
      task: results.tasks.map(task => taskRow(task, words)),
      project: searchProjectList(projects ?? [], results.query, options).map(project => projectRow(project)),
      note: results.notes.map(note => noteRow(note, words)).filter((row): row is Row => row !== null),
      focus: searchTaskList(latest?.focused ?? [], results.query, { ...options, limit: LIMITS.task }).map(task => focusRow(task, words)),
    }
  }, [results, words, includeCompleted, activeLifeSpace, projects, latest, taskRow, projectRow, noteRow, focusRow])

  // Zuerst die zuletzt geöffneten, aufgefüllt mit den zuletzt geänderten; `null`, solange sie laden.
  const latestRows = useMemo<Row[] | null>(() => {
    if (kind === 'all' || !latest) return null
    const firstOpened = <T extends { id: string }>(opened: T[], changed: T[]) => {
      const seen = new Set<string>()
      return [...opened, ...changed]
        .filter((item) => {
          if (seen.has(item.id)) return false
          seen.add(item.id)
          return true
        })
        .slice(0, LATEST)
    }
    if (kind === 'task') {
      const opened = openedTasks.filter(task => includeCompleted || isOpenTask(task))
      return firstOpened(opened, latest.tasks).map(task => taskRow(task, []))
    }
    if (kind === 'project') {
      const opened = openedProjects.filter(project => !project.isSystem && (includeCompleted || isActiveProject(project)))
      return firstOpened(opened, latestProjectList(projects ?? [], { includeCompleted, limit: LATEST })).map(project => projectRow(project))
    }
    if (kind === 'focus') return latest.focused.slice(0, LATEST).map(task => focusRow(task, []))
    return latest.notes.map(note => noteRow(note, [])).filter((row): row is Row => row !== null)
  }, [kind, latest, openedTasks, openedProjects, projects, includeCompleted, taskRow, projectRow, noteRow, focusRow])

  const sections = useMemo<Section[]>(() => {
    const actionSpace = isLifeSpace(activeLifeSpace) ? activeLifeSpace : null
    // Ohne Überschrift: Die gewählte Art sagt, was da steht.
    if (!groups && kind !== 'all') return latestRows?.length ? [{ key: `latest-${kind}`, heading: '', rows: latestRows }] : []
    if (!groups) {
      return [
        { key: 'recent-tasks', heading: t('search.groups.recentTasks'), rows: recentTasks.map(task => taskRow(task, [], 'recent-')) },
        { key: 'recent-projects', heading: t('search.groups.recentProjects'), rows: recentProjects.map(project => projectRow(project, 'recent-')) },
        { key: 'recent-focus', heading: t('search.groups.recentFocus'), rows: (latest?.focused ?? []).slice(0, RECENT_FOCUS).map(task => focusRow(task, [], 'recent-')) },
        { key: 'actions', heading: t('search.groups.actions'), rows: actionSpace ? [actionRow('upcoming', actionSpace)] : [] },
      ].filter(section => section.rows.length > 0)
    }
    const headings = { task: t('search.groups.tasks'), project: t('search.groups.projects'), note: t('search.groups.board'), focus: t('search.groups.recentFocus') }
    const kindNames = { task: t('search.kinds.task'), project: t('search.kinds.project'), note: t('search.kinds.note') }
    if (kind !== 'all') return [{ key: kind, heading: headings[kind], rows: groups[kind] }].filter(section => section.rows.length > 0)
    const actions = actionSpace
      ? ACTION_PAGES.map(page => actionRow(page, actionSpace)).filter(row => rankText(row.title, '', words) !== null)
      : []
    return [
      ...(['task', 'project', 'note'] as const).map(groupKind => {
        const rows = groups[groupKind]
        const more: Row[] = rows.length > PREVIEW
          ? [{
            value: `more:${groupKind}`,
            moreKind: groupKind,
            icon: null,
            title: t('search.showAll', { count: rows.length, kind: kindNames[groupKind] }),
            location: '',
            spaceLabel: '',
            done: false,
          }]
          : []
        return { key: groupKind, heading: headings[groupKind], rows: [...rows.slice(0, PREVIEW), ...more] }
      }),
      { key: 'actions', heading: t('search.groups.actions'), rows: actions },
    ].filter(section => section.rows.length > 0)
  }, [groups, kind, words, activeLifeSpace, latestRows, latest, recentTasks, recentProjects, taskRow, projectRow, focusRow, actionRow, t])

  const rows = useMemo(() => sections.flatMap(section => section.rows), [sections])
  const counts = groups
    ? {
      all: groups.task.length + groups.project.length + groups.note.length,
      task: groups.task.length,
      project: groups.project.length,
      note: groups.note.length,
      focus: groups.focus.length,
    }
    : null

  // Gebuchte Sekunden der sichtbaren Fokus-Zeilen — eine Abfrage für alle; den laufenden Anteil zählt `FocusTime`.
  const focusIds = useMemo(() => [...new Set(rows.flatMap(row => (row.focusTime ? [row.focusTime.taskId] : [])))], [rows])
  const bookedSeconds = source.useFocusSeconds(focusIds)

  /*
    ── Auswahl: neue Treffer, eine andere Art oder eine neue oberste Zeile beginnen oben ──
    Die oberste Zeile zählt mit: „Zuletzt geöffnet“ kommt erst einen Moment nach dem Öffnen aus der
    Datenbank. Bis dahin steht nur die Aktion da — ohne das bliebe die Auswahl ganz unten an ihr
    hängen (gemeldet 02.10.2026).
  */
  const [selected, setSelected] = useState('')
  const firstValue = rows[0]?.value ?? ''
  useEffect(() => { setSelected(firstValue) }, [results, kind, firstValue])
  useEffect(() => {
    if (!rows.some(row => row.value === selected)) setSelected(firstValue)
  }, [rows, selected, firstValue])

  /* ── Öffnen, Fokus, Art wechseln ── */
  const activate = useCallback((row: Row, { newTab, focus }: { newTab: boolean; focus: boolean }) => {
    if (row.moreKind) {
      setKind(row.moreKind)
      return
    }
    if (focus) {
      if (!row.focusTaskId) return
      onStartFocus(row.focusTaskId)
      onOpenChange(false)
      return
    }
    if (!row.hit) return
    onSelect(row.hit, { newTab })
    onOpenChange(false)
  }, [onSelect, onStartFocus, onOpenChange])

  // cmdk meldet einen Klick ohne Ereignis — die Tasten dazu merkt sich die Erfassungsphase vorher.
  const clickMods = useRef<{ alt: boolean; mod: boolean } | null>(null)

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    clickMods.current = null
    const plainArrow = (e.key === 'ArrowLeft' || e.key === 'ArrowRight')
      && !(e.shiftKey || e.altKey || e.metaKey || e.ctrlKey || e.nativeEvent.isComposing)
    if (plainArrow) {
      /*
        ←/→ gehören der Trefferart, nicht dem Caret (gewünscht 02.10.2026, vorher ⇥) — auch vor dem
        Tippen: Die gewählte Art gilt dann, sobald man tippt. Den Caret schieben ⌥/⌘ mit Pfeil.
      */
      e.preventDefault()
      const step = e.key === 'ArrowRight' ? 1 : KINDS.length - 1
      setKind(current => KINDS[(KINDS.indexOf(current) + step) % KINDS.length]!)
      return
    }
    if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey || e.altKey)) return
    // Vor cmdk: Ein schlichtes ↵ öffnet über `onSelect` der Zeile, mit Taste entscheiden wir hier.
    e.preventDefault()
    const row = rows.find(r => r.value === selected)
    if (row) activate(row, { newTab: e.altKey, focus: e.metaKey || e.ctrlKey })
  }, [rows, selected, activate])

  const toggleCompleted = useCallback((checked: boolean) => {
    setIncludeCompleted(checked)
    inputRef.current?.focus()
  }, [])

  const hasQuery = searchWords(query).length > 0
  const nothingFound = results !== null && hasQuery && rows.length === 0
  const nothingYet = !hasQuery && latestRows !== null && latestRows.length === 0

  return (
    <Command
      className="command-palette"
      label={t('search.label')}
      shouldFilter={false}
      vimBindings={false}
      // Wie in Raycast: Die Maus markiert nicht um — die Tastatur-Auswahl bleibt stehen, Überfahren
      // tönt nur leicht grau (CSS `:hover`). Ein Klick öffnet weiter (gewünscht 02.10.2026).
      disablePointerSelection
      loop
      value={selected}
      onValueChange={setSelected}
      onKeyDown={handleKeyDown}
      onClickCapture={e => { clickMods.current = { alt: e.altKey, mod: e.metaKey || e.ctrlKey } }}
    >
      <div className="command-palette__field">
        <Search size={ICON} className="command-palette__field-icon" aria-hidden="true" />
        <Command.Input
          ref={inputRef}
          className="command-palette__input"
          value={query}
          onValueChange={setQuery}
          placeholder={t('search.placeholder')}
          autoFocus
          spellCheck={false}
        />
      </div>

      <div className="command-palette__kinds">
        <div className="command-palette__kind-list" role="group" aria-label={t('search.kindsLabel')}>
          {KINDS.map(k => (
            <React.Fragment key={k}>
              <button
                type="button"
                className={`command-palette__kind${kind === k ? ' command-palette__kind--selected' : ''}`}
                aria-pressed={kind === k}
                // Der Fokus bleibt im Feld — sonst tippte man nach dem Klick ins Leere. Die Tastatur
                // erreicht die Art über ←/→ im Feld, ⇥ springt deshalb gleich zum Schalter.
                tabIndex={-1}
                onMouseDown={e => e.preventDefault()}
                onClick={() => setKind(k)}
              >
                {t(`search.kinds.${k}`)}
                {counts && <span className="command-palette__kind-count">{counts[k]}</span>}
              </button>
              {k === 'focus' && <span className="command-palette__kind-divider" aria-hidden="true" />}
            </React.Fragment>
          ))}
        </div>
        {/*
          Mit ⇥ erreicht, gehören die Tasten dem Schalter: ↵ öffnete über cmdk sonst den markierten
          Treffer, ↑/↓ verschöben die Markierung. Esc schließt weiter die Suche.
        */}
        <label
          className="command-palette__completed"
          onKeyDown={e => { if (e.key !== 'Escape') e.stopPropagation() }}
        >
          <Switch checked={includeCompleted} onCheckedChange={toggleCompleted} className="command-palette__completed-switch" />
          {t('search.showCompleted')}
        </label>
      </div>

      <Command.List
        ref={listRef}
        className={`command-palette__list${scrollbarVisible ? ' command-palette__list--scroll-visible' : ''}`}
        onScroll={onListScroll}
      >
        {sections.map(section => (
          <Command.Group key={section.key} heading={section.heading} className="command-palette__group">
            {section.rows.map(row => (row.moreKind
              ? (
                <Command.Item key={row.value} value={row.value} className="command-palette__more" onSelect={() => activate(row, { newTab: false, focus: false })}>
                  {row.title}
                </Command.Item>
              )
              : (
                <Command.Item
                  key={row.value}
                  value={row.value}
                  className={`command-palette__row${row.done ? ' command-palette__row--done' : ''}`}
                  data-space={row.lifeSpace}
                  onSelect={() => {
                    const mods = clickMods.current
                    clickMods.current = null
                    activate(row, { newTab: mods?.alt ?? false, focus: mods?.mod ?? false })
                  }}
                >
                  <span className={`command-palette__row-icon${row.tintIcon ? ' command-palette__row-icon--space' : ''}`} aria-hidden="true">{row.icon}</span>
                  <span className="command-palette__row-text">
                    <span className="command-palette__row-line">
                      <span className="command-palette__row-title"><Highlight text={row.title} words={words} /></span>
                      {row.location && <span className="command-palette__row-location">{row.location}</span>}
                      {row.spaceLabel && <span className="command-palette__row-space">{row.spaceLabel}</span>}
                    </span>
                    {row.excerpt && <span className="command-palette__row-excerpt"><Highlight text={row.excerpt} words={words} /></span>}
                  </span>
                  {row.focusTime && (
                    <FocusTime
                      booked={bookedSeconds?.get(row.focusTime.taskId) ?? 0}
                      estimate={row.focusTime.estimate}
                      runningSince={row.focusTime.runningSince}
                    />
                  )}
                  {row.focusTime?.runningSince && <span className="command-palette__row-running">{t('search.running')}</span>}
                  {/* Nur an der markierten Zeile sichtbar, aber immer im Fluss — die Zeile springt beim Wandern nicht. */}
                  <span className="command-palette__row-hint">
                    {row.focusTaskId && (
                      <span className="command-palette__row-focus" aria-label={t('search.startFocus')}>
                        <Play size={11} aria-hidden="true" />
                        <ShortcutBadge variant="outline" shortcut={{ kind: 'return', mod: true }} />
                      </span>
                    )}
                    <ShortcutBadge variant="outline" shortcut={{ kind: 'return' }} />
                  </span>
                </Command.Item>
              )))}
          </Command.Group>
        ))}
        {nothingYet && (
          <div className="command-palette__empty">
            {t('search.nothingYet')}
            {!includeCompleted && <span className="command-palette__empty-hint"> {t('search.completedHidden')}</span>}
          </div>
        )}
        {nothingFound && (
          <div className="command-palette__empty">
            {/* Unter einer einzelnen Art mit Treffern in anderen: hier ist nichts, nicht „es gibt nichts“. */}
            {kind !== 'all' && counts && counts.all > 0
              ? t('search.emptyKind', { kind: t(`search.kinds.${kind}`), query: query.trim() })
              : t('search.empty', { query: query.trim() })}
            {!includeCompleted && <span className="command-palette__empty-hint"> {t('search.completedHidden')}</span>}
          </div>
        )}
      </Command.List>

      <div className="command-palette__footer" aria-hidden="true">
        <div className="command-palette__footer-item">
          <ShortcutBadge variant="subtle" shortcut={[{ kind: 'character', text: '↑' }, { kind: 'character', text: '↓' }]} />
          {t('search.footer.navigate')}
        </div>
        <div className="command-palette__footer-item">
          <ShortcutBadge variant="subtle" shortcut={{ kind: 'return' }} />
          {t('search.footer.open')}
        </div>
        <div className="command-palette__footer-item">
          <ShortcutBadge variant="subtle" shortcut={[{ kind: 'character', text: isMac ? '⌥' : 'Alt' }, { kind: 'return' }]} />
          {t('search.footer.newTab')}
        </div>
        <div className="command-palette__footer-item">
          <ShortcutBadge variant="subtle" shortcut={[{ kind: 'character', text: '←' }, { kind: 'character', text: '→' }]} />
          {t('search.footer.kind')}
        </div>
        <div className="command-palette__footer-item command-palette__footer-end">
          <ShortcutBadge variant="subtle" shortcut={{ kind: 'character', text: 'Esc' }} />
          {t('search.footer.close')}
        </div>
      </div>
    </Command>
  )
}
